import { useId, useMemo, useRef, useState } from 'react'
import Icon, { type IconName } from '../../docs/Icon'

export type ComboBoxProps = {
  label: string
  options: string[]
  hint?: string
  value?: string
  onChange?: (value: string) => void
  /** Removes the id so a preview copy does not repeat it. */
  id?: string
  /** Where the hint shows; above the field by default. */
  hintPlacement?: 'above' | 'placeholder' | 'both'
  /** The toggle's icon; a chevron by default. */
  icon?: IconName
}

/**
 * A text field that filters a list as you type: the ARIA 1.2 combobox with a
 * listbox popup. Arrow keys move through matches, Enter chooses, Escape closes
 * and then clears.
 */
export default function ComboBox({ label, options, hint, value = '', onChange, id, icon = 'chevron', hintPlacement = 'above' }: ComboBoxProps) {
  const autoId = useId()
  const fieldId = id ?? autoId
  const listId = `${fieldId}-list`
  const hintId = `${fieldId}-hint`
  const [query, setQuery] = useState(value)
  // A new value from the parent, such as a reset, replaces what is typed.
  const [given, setGiven] = useState(value)
  if (value !== given) {
    setGiven(value)
    setQuery(value)
  }
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const input = useRef<HTMLInputElement>(null)

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return needle ? options.filter((option) => option.toLowerCase().includes(needle)) : options
  }, [options, query])

  const choose = (option: string) => {
    setQuery(option)
    setOpen(false)
    setActive(-1)
    onChange?.(option)
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setOpen(true)
      setActive((index) => Math.min(matches.length - 1, index + 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((index) => Math.max(0, index - 1))
    } else if (event.key === 'Enter' && open && active >= 0 && matches[active]) {
      event.preventDefault()
      choose(matches[active])
    } else if (event.key === 'Escape') {
      if (open) setOpen(false)
      else {
        setQuery('')
        onChange?.('')
      }
      setActive(-1)
    }
  }

  return (
    <div className="mds-field mds-combo">
      <label className="mds-label" htmlFor={fieldId}>
        {label}
      </label>
      {hint && (
        <span className={hintPlacement === 'placeholder' ? 'mds-visually-hidden' : 'mds-hint'} id={hintId}>
          {hint}
        </span>
      )}
      <div className="mds-combo__control">
        <input
          ref={input}
          id={fieldId}
          className="mds-input mds-combo__input"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
          aria-describedby={hint ? hintId : undefined}
          autoComplete="off"
          placeholder={hint && hintPlacement !== 'above' ? hint : undefined}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value)
            setOpen(true)
            setActive(-1)
          }}
          onKeyDown={onKeyDown}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        />
        <button
          type="button"
          className="mds-combo__toggle"
          tabIndex={-1}
          aria-label={`Show ${label.toLowerCase()} options`}
          // Keeps focus in the input, so its blur does not close the list just opened.
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            setOpen((state) => !state)
            input.current?.focus()
          }}
        >
          <Icon name={icon} size={icon === 'chevron' ? 16 : 18} className={icon === 'chevron' ? 'mds-combo__chevron' : undefined} />
        </button>
      </div>
      <ul className="mds-combo__list" id={listId} role="listbox" aria-label={label} hidden={!open || matches.length === 0}>
        {matches.map((option, index) => (
          <li
            key={option}
            id={`${listId}-${index}`}
            role="option"
            aria-selected={index === active}
            className="mds-combo__option"
            onMouseDown={(event) => {
              event.preventDefault()
              choose(option)
            }}
          >
            {option}
          </li>
        ))}
      </ul>
      <p className="mds-combo__empty" role="status">
        {open && matches.length === 0 ? 'No matches' : ''}
      </p>
    </div>
  )
}
